import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAdminStore } from "@/stores/admin-store"
import { CreateOrgDialog } from "@/components/admin/CreateOrgDialog"

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

const STATUS_OPTIONS = ["pending", "contacted", "converted", "rejected"] as const

const statusVariant: Record<string, "default" | "warning" | "success" | "destructive"> = {
  pending: "warning",
  contacted: "default",
  converted: "success",
  rejected: "destructive",
}

const FILTER_TABS = [
  { label: "All", value: undefined },
  { label: "Pending", value: "pending" },
  { label: "Contacted", value: "contacted" },
  { label: "Converted", value: "converted" },
  { label: "Rejected", value: "rejected" },
] as const

export function AdminDemoRequestsPage() {
  const {
    demoRequests,
    isDemoRequestsLoading,
    demoRequestsFilter,
    loadDemoRequests,
    updateDemoRequestStatus,
    convertDemoRequest,
  } = useAdminStore()

  const [convertingRequest, setConvertingRequest] = useState<{
    id: string
    company: string
    email: string
  } | null>(null)

  useEffect(() => {
    void loadDemoRequests()
  }, [loadDemoRequests])

  const handleFilterChange = (value: string | undefined) => {
    void loadDemoRequests(value)
  }

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await updateDemoRequestStatus(id, newStatus)
    } catch {
      // Error is set in store
    }
  }

  const handleConvert = async (organizationName: string, adminEmail: string, role: string) => {
    if (!convertingRequest) throw new Error("No request selected")
    const result = await convertDemoRequest(convertingRequest.id, organizationName, adminEmail, role)
    return { inviteUrl: result.inviteUrl }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-1">
          {FILTER_TABS.map((tab) => (
            <Button
              key={tab.label}
              variant={demoRequestsFilter === tab.value ? "default" : "ghost"}
              size="sm"
              onClick={() => handleFilterChange(tab.value)}
              className="text-xs"
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Table */}
        {isDemoRequestsLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : demoRequests.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No demo requests found.
          </p>
        ) : (
          <div className="border rounded-lg overflow-hidden bg-card">
            <table className="w-full text-sm table-fixed">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[140px]">Name</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[200px]">Email</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[150px]">Company</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[130px]">Phone</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Message</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[110px]">Status</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[110px]">Date</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground w-[200px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {demoRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-muted/30 align-top">
                    <td className="px-3 py-2 break-words">{`${request.firstName} ${request.lastName}`}</td>
                    <td className="px-3 py-2 text-muted-foreground break-words">{request.email}</td>
                    <td className="px-3 py-2 text-muted-foreground break-words">{request.company ?? "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground break-words">{request.phone ?? "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-pre-wrap break-words">
                      {request.message ?? "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={statusVariant[request.status] ?? "default"}>
                        {request.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{formatDate(request.createdAt)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {request.status !== "converted" && (
                          <Select
                            value={request.status}
                            onValueChange={(value) => void handleStatusChange(request.id, value)}
                          >
                            <SelectTrigger className="h-7 w-[110px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((status) => (
                                <SelectItem key={status} value={status} className="text-xs">
                                  {status}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {(request.status === "pending" || request.status === "contacted") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7"
                            onClick={() =>
                              setConvertingRequest({
                                id: request.id,
                                company: request.company,
                                email: request.email,
                              })
                            }
                          >
                            Convert
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Convert Dialog */}
      <CreateOrgDialog
        open={!!convertingRequest}
        onOpenChange={(open) => {
          if (!open) setConvertingRequest(null)
        }}
        onSubmit={handleConvert}
        defaultOrgName={convertingRequest?.company ?? ""}
        defaultEmail={convertingRequest?.email ?? ""}
        title="Convert to Organisation"
        description="Create an organisation from this demo request and send an admin invitation."
      />
    </div>
  )
}
